import { Component, Input, OnInit } from '@angular/core';
import {
  NgbActiveModal,
  NgbModule,
  NgbModal
} from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { IntegerFormatPipe } from '@core/pipe/integer-format.pipe';
import { RestClientService } from '@core/service/rest-client.service';
import { UtilitiesService } from '@core/service/utilities.service';
import { AuthService } from '@core/service/auth.service';
import { BsDatepickerModule, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { frLocale } from 'ngx-bootstrap/locale';
import { FormDialogComponent as ModalAddClient } from '../../../administration/utilisateur-client/form-dialog/form-dialog.component';
import { PrintService } from '@core/service/print.service';
import { Direction } from '@angular/cdk/bidi';
import Swal from 'sweetalert2';

export interface DialogData {
  id: number;
  action: string;
  item: any;
}

defineLocale('fr', frLocale);

@Component({
  selector: 'app-modal-commande-boutique',
  imports: [
    CommonModule,
    FormsModule,
    IntegerFormatPipe,
    NgSelectModule,
    NgbModule,
    BsDatepickerModule,
  ],
  templateUrl: './modal-commande-boutique.component.html',
  styleUrl: './modal-commande-boutique.component.scss',
})
export class ModalCommandeBoutiqueComponent implements OnInit {
  @Input() data: any;

  action = '';
  itemToSave: any = {};
  isLoading = false;
  user: any;
  title = 'commande boutique';
  createMessage = '';
  editMessage = '';
  endPoint = 'commande';

  itemsArticle: any[] = [];
  itemsCommandeDetailToSave: any[] = [];
  itemsTypeCommande: any[] = [];
  itemsMagasin: any[] = [];

  article_search = '';

  constructor(
    public activeModal: NgbActiveModal,
    private restClient: RestClientService,
    private utilities: UtilitiesService,
    private authService: AuthService,
    private localeService: BsLocaleService,
    private printService: PrintService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.itemToSave = { ...this.data };
    this.user = this.authService.getCurrentUser();
    this.localeService.use('fr');
    this.createMessage = 'Vous êtes sur le point de créer une ' + this.title;
    this.editMessage = 'Vous êtes sur le point de modifier une ' + this.title;
    this.getItemsTypeCommande();
    this.getItemsMagasin();
  }

  submit() {}

  // ── Article grid ──────────────────────────────────────────────────────────
  get filteredArticles(): any[] {
    if (!this.article_search) return this.itemsArticle;
    const key = this.article_search.toLowerCase();
    return this.itemsArticle.filter((a: any) =>
      (a.libelle || '').toLowerCase().includes(key)
    );
  }

  // ── Cart helpers ──────────────────────────────────────────────────────────
  isInCart(article: any): boolean {
    return this.itemsCommandeDetailToSave.some(
      (i: any) => i.article_id === article.id
    );
  }

  getCartQty(article: any): number {
    const found = this.itemsCommandeDetailToSave.find(
      (i: any) => i.article_id === article.id
    );
    return found ? found.quantite : 0;
  }

  addToCart(article: any) {
    if (article.quantite_en_stock <= 0) return;
    const existing = this.itemsCommandeDetailToSave.find(
      (i: any) => i.article_id === article.id
    );
    if (existing) {
      if (existing.quantite < article.quantite_en_stock) {
        existing.quantite++;
        existing.total = existing.quantite * existing.prix;
      }
    } else {
      this.itemsCommandeDetailToSave.push({
        article_id: article.id,
        article_libelle: article.libelle,
        article_description: article.description,
        article_image_url: article.image_url,
        magasin_id: article.magasin_id,
        prix: article.prix_vente,
        article_prix: article.prix_vente,
        quantite: 1,
        total: article.prix_vente,
      });
    }
  }

  incrementCartQty(item: any) {
    const art = this.itemsArticle.find((a: any) => a.id === item.article_id);
    if (art && item.quantite < art.quantite_en_stock) {
      item.quantite++;
      item.total = item.quantite * item.prix;
    }
  }

  decrementCartQty(item: any, index: number) {
    if (item.quantite > 1) {
      item.quantite--;
      item.total = item.quantite * item.prix;
    } else {
      this.itemsCommandeDetailToSave.splice(index, 1);
    }
  }

  onCartQtyChange(item: any) {
    const art = this.itemsArticle.find((a: any) => a.id === item.article_id);
    if (!item.quantite || item.quantite < 1) {
      item.quantite = 1;
    } else if (art && item.quantite > art.quantite_en_stock) {
      item.quantite = art.quantite_en_stock;
      this.utilities.showNotification(
        'snackbar-danger',
        'Quantité disponible: ' + art.quantite_en_stock,
        'bottom',
        'center'
      );
    }
    item.total = item.quantite * item.prix;
  }

  get cartTotal(): number {
    return this.itemsCommandeDetailToSave.reduce(
      (sum: number, i: any) => sum + (i.total || 0),
      0
    );
  }

  // ── API calls ─────────────────────────────────────────────────────────────
  getItemsTypeCommande() {
    this.restClient
      .post('typeCommande/getByCriteria', {
        data: { codification: 'BOUTIQUE' },
        index: 0,
        size: 10000,
      })
      .subscribe((res: any) => {
        if (res && res['items'] && !res['hasError']) {
          this.itemsTypeCommande = res['items'];
          this.itemToSave.type_commande_id = this.itemsTypeCommande[0]?.id;
        }
      });
  }

  getItemsMagasin() {
    this.restClient
      .post('magasin/getByCriteria', { data: {}, index: 0, size: 10000 })
      .subscribe((res: any) => {
        this.itemsMagasin = res && res['items'] && !res['hasError'] ? res['items'] : [];
      });
  }

  onSelectMagasin() {
    this.itemsArticle = [];
    this.getItemsArticle();
  }

  getItemsArticle() {
    if (!this.itemToSave.magasin_stock_id) return;
    this.restClient
      .post('article/getByCriteria', {
        is_for_commande_boutique: true,
        add_stock_info: true,
        magasin_stock_id: this.itemToSave.magasin_stock_id,
        data: {},
        index: 0,
        size: 10000,
      })
      .subscribe((res: any) => {
        if (res && res['items'] && !res['hasError']) {
          this.itemsArticle = res['items'];
          this.itemsArticle.forEach((a: any) => {
            a.magasin_id = this.itemToSave.magasin_stock_id;
            a.libelle_stock = a.libelle + ' (' + a.quantite_en_stock + ')';
          });
        }
      });
  }

  confirmSaveItem(item: any) {
    if (!item.id) item.code = this.generateUniqueString();
    const objToSave = Object.assign({}, item);
    Swal.fire({
      title: this.capitalize(this.title),
      text: objToSave?.id ? this.editMessage : this.createMessage,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0665aa',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
    }).then((result) => {
      if (result.value) this.saveItem(objToSave);
    });
  }

  saveItem(item: any) {
    this.isLoading = true;
    let itemAEnregistrer = Object.assign({}, item);

    let montant = 0;
    let quantite = 0;
    if (this.itemsCommandeDetailToSave?.length) {
      itemAEnregistrer.commandeDetail = this.itemsCommandeDetailToSave;
      itemAEnregistrer.commandeDetail.forEach((iae: any) => {
        iae.type_commande_id = this.itemToSave.type_commande_id;
        iae.hebergement_id = this.itemToSave.hebergement_id;
        iae.chambre_id = this.itemToSave.chambre_id;
        iae.client_id = this.itemToSave.client_id;
        montant += iae.total;
        quantite += iae.quantite;
      });
    }

    itemAEnregistrer.quantite = quantite;
    itemAEnregistrer.montant = montant;
    itemAEnregistrer.type_commande_id = this.itemToSave.type_commande_id;
    itemAEnregistrer.nombre = this.itemsCommandeDetailToSave.length;
    itemAEnregistrer.is_client = true;
    const hebergementId = itemAEnregistrer.id;
    itemAEnregistrer.hebergement_id = hebergementId;
    delete itemAEnregistrer.id;

    this.restClient
      .post(this.endPoint + '/create', { datas: [itemAEnregistrer] })
      .subscribe(
        (res: any) => {
          this.isLoading = false;
          if (!res['hasError']) {
            this.utilities.showNotification(
              'snackbar-success',
              this.utilities.formatMsgServeur(res['status']['message']),
              'bottom',
              'center'
            );
            this.activeModal.close('saved');
            this.onPrintRecuCommande(res['item']);
          } else if (res['status']?.message) {
            this.utilities.showNotification(
              'snackbar-danger',
              this.utilities.formatMsgServeur(res['status']['message']),
              'bottom',
              'center'
            );
          }
        },
        () => {
          this.isLoading = false;
          this.utilities.showNotification(
            'snackbar-danger',
            this.utilities.getMessageEndPointNotAvailble(),
            'bottom',
            'center'
          );
        }
      );
  }

  openAddClientModal(action: 'add' | 'edit', data?: any) {
    let varDirection: Direction =
      localStorage.getItem('isRtl') === 'true' ? 'rtl' : 'ltr';
    const modalRef = this.modalService.open(ModalAddClient, {
      size: 'xl',
      windowClass: 'modal-xxl',
      centered: true,
    });
    modalRef.componentInstance.data = { item: data, action };
    modalRef.closed.subscribe((result) => {
      if (result && result.id) {
        this.itemToSave.client_id = result.id;
      }
    });
  }

  generateUniqueString() {
    return (
      new Date().getTime().toString() +
      Math.floor(Math.random() * 1000000).toString()
    );
  }

  onPrintRecuCommande(item: any) {
    let request = {
      data: {
        id: item.id,
        entite: 'BOUTIQUE',
      },
      index: 0,
      size: 10000,
    };

    this.restClient.post('pdf/printRecuCommande', request).subscribe(
      (res: any) => {
        if (res && res['item'] && !res['hasError']) {
          this.printService.previewPdf(res['item'].link);
        } else {
          this.utilities.showNotification(
            'snackbar-danger',
            this.utilities.formatMsgServeur(res['status']['message']),
            'bottom',
            'center'
          );
        }
      },
      (err) => {}
    );
  }

  capitalize(str: any) {
    return str.replace(/^\w/, (c: string) => c.toUpperCase());
  }
}
